import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { AuthModal } from '../components/auth/AuthModal';

export type AuthModalMode = 'login' | 'register';

interface OpenOptions {
  mode?: AuthModalMode;
  onSuccess?: () => void;
}

interface AuthModalContextValue {
  open: (options?: OpenOptions) => void;
  close: () => void;
}

const AuthModalContext = createContext<AuthModalContextValue | null>(null);

interface State {
  open: boolean;
  mode: AuthModalMode;
  onSuccess?: () => void;
}

export function AuthModalProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<State>({ open: false, mode: 'login' });

  const open = useCallback((options: OpenOptions = {}) => {
    setState({ open: true, mode: options.mode ?? 'login', onSuccess: options.onSuccess });
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, open: false }));
  }, []);

  const setMode = useCallback((mode: AuthModalMode) => {
    setState((prev) => ({ ...prev, mode }));
  }, []);

  const handleSuccess = useCallback(() => {
    const cb = state.onSuccess;
    setState((prev) => ({ ...prev, open: false }));
    if (cb) cb();
  }, [state.onSuccess]);

  const value = useMemo<AuthModalContextValue>(() => ({ open, close }), [open, close]);

  return (
    <AuthModalContext.Provider value={value}>
      {children}
      <AuthModal
        open={state.open}
        mode={state.mode}
        onClose={close}
        onModeChange={setMode}
        onSuccess={handleSuccess}
      />
    </AuthModalContext.Provider>
  );
}

export function useAuthModal() {
  const ctx = useContext(AuthModalContext);
  if (!ctx) {
    throw new Error('useAuthModal must be used inside <AuthModalProvider>');
  }
  return ctx;
}
